import React, { Suspense } from 'react';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canAccessMenu } from '../utils/menuAccess';
import SimpleLayout from '../components/ui/SimpleLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import Spinner from '../components/ui/Spinner';
import { MANAGE_DEFAULT_PATH } from '../pages/manage/manageCatalog';
import { MEETING_NOTES_ENABLED } from '../utils/featureFlags'; // 회의록 기능 게이트(기본 OFF)

// 중요 페이지는 즉시 로드
import Login from '../pages/auth/LoginPageNew';
import AdminDashboard from '../pages/dashboard/AdminDashboard';

// 나머지는 지연 로딩
const Register = lazyWithRetry(() => import('../pages/auth/RegisterPageNew'));
const ForgotPassword = lazyWithRetry(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPassword = lazyWithRetry(() => import('../pages/auth/ResetPasswordPage'));
const AccessDenied = lazyWithRetry(() => import('../pages/AccessDeniedPage'));

// 외부 고객 주문/배송 조회 (비로그인, 휴대폰 인증 기반 — 일반 메뉴 완전 격리)
const CtPhoneInputPage = lazyWithRetry(() => import('../pages/customer-tracking/PhoneInputPage'));
const CtCodeVerifyPage = lazyWithRetry(() => import('../pages/customer-tracking/CodeVerifyPage'));
const CtOrderListPage = lazyWithRetry(() => import('../pages/customer-tracking/OrderListPage'));
const CtOrderDetailPage = lazyWithRetry(() => import('../pages/customer-tracking/OrderDetailPage'));
const RequireCustomerSession = lazyWithRetry(() => import('../pages/customer-tracking/RequireCustomerSession'));
// 고객 설치요청 흐름 (멀티스텝 위저드 + 완료)
const InstallRequestWizard = lazyWithRetry(() => import('../pages/installation-request/InstallRequestWizard'));
const InstallRequestDonePage = lazyWithRetry(() => import('../pages/installation-request/InstallRequestDonePage'));
const MyInstallSchedulesPage = lazyWithRetry(() => import('../pages/installation-request/MyInstallSchedulesPage'));
const MyInstallScheduleDetailPage = lazyWithRetry(() => import('../pages/installation-request/MyInstallScheduleDetailPage'));

// 구매 관리 페이지들
const PurchaseListPage = lazyWithRetry(() => import('../pages/purchase/PurchaseListPage'));
const PurchaseFormPage = lazyWithRetry(() => import('../pages/purchase/PurchaseFormPage'));
const PurchaseOrderView = lazyWithRetry(() => import('../pages/purchase/PurchaseOrderView'));
const BulkPrintPage = lazyWithRetry(() => import('../pages/purchase/BulkPrintPage'));

// 생산 관리 페이지들
const ProductionListPage = lazyWithRetry(() => import('../pages/production/ProductionListPage'));
const ProductionFormPage = lazyWithRetry(() => import('../pages/production/ProductionFormPage'));
const ProductionPrintPage = lazyWithRetry(() => import('../pages/production/ProductionPrintPage'));
const ProductionOrderView = lazyWithRetry(() => import('../pages/production/ProductionOrderView'));

// 판매 관리 페이지들
const SalesListPage = lazyWithRetry(() => import('../pages/sales/SalesListPage'));
const SalesFormPage = lazyWithRetry(() => import('../pages/sales/SalesFormPage'));
const SalesViewPage = lazyWithRetry(() => import('../pages/sales/SalesViewPage'));
const SalesOrderView = lazyWithRetry(() => import('../pages/sales/SalesOrderView'));

// 주요 모듈들
const ContactListPage = lazyWithRetry(() => import('../pages/contacts/ContactListPage'));
const ProductListPage = lazyWithRetry(() => import('../pages/inventory/ProductListPage'));
const InventoryHistoryPage = lazyWithRetry(() => import('../pages/inventory/InventoryHistoryPage'));
const InventoryCountListPage = lazyWithRetry(() => import('../pages/inventory/InventoryCountListPage'));
const BOMManagement = lazyWithRetry(() => import('../pages/BOMManagement'));
const HappyCallManagement = lazyWithRetry(() => import('../pages/HappyCallKanban'));
const HomeStylePage = lazyWithRetry(() => import('../pages/HomeStylePage'));
const HomeStylingApplyPage = lazyWithRetry(() => import('../pages/public/HomeStylingApplyPage'));
const RepairApplyPage = lazyWithRetry(() => import('../pages/public/RepairApplyPage'));
const OrderConsentPage = lazyWithRetry(() => import('../pages/public/OrderConsentPage'));
const RenderOrderSheetPage = lazyWithRetry(() => import('../pages/public/RenderOrderSheetPage'));
const RepairRequestsPage = lazyWithRetry(() => import('../pages/repair/RepairRequestsPage'));
const McpApprovalsPage = lazyWithRetry(() => import('../pages/mcp-approvals/McpApprovalsPage'));
const InstallationSchedulingPage = lazyWithRetry(() => import('../pages/installation/InstallationSchedulingPage'));
const ClaimsPage = lazyWithRetry(() => import('../pages/claims/ClaimsPage'));
// RefurbInventoryPage는 재고 탭으로 이동됨 (RefurbTab)
const AnalyticsDashboard = lazyWithRetry(() => import('../components/analytics/AnalyticsDashboard'));
// [F12] 인사이트→통계 대시보드(StatisticsPage) 프론트 제거 — 통계는 각 업무 페이지(해피콜 등) 내부 뷰로 통합. 백엔드 통계 API 는 유지.
const OrderCollectionPage = lazyWithRetry(() => import('../pages/automation/OrderCollectionPage'));
const AutomationTasksPage = lazyWithRetry(() => import('../pages/automation/AutomationTasksPage'));
// 회의록 녹음 (S6) — ★기능 게이트(기본 OFF)★ MEETING_NOTES_ENABLED='true' 빌드에서만 라우트 등록 (import는 상단)
const MeetingRecordPage = lazyWithRetry(() => import('../pages/meeting-notes/MeetingRecordPage'));
const TranscriptReviewPage = lazyWithRetry(() => import('../pages/meeting-notes/TranscriptReviewPage'));
const MeetingDictionaryPage = lazyWithRetry(() => import('../pages/meeting-notes/DictionaryPage'));
const NotificationsPage = lazyWithRetry(() => import('../pages/NotificationsPage'));

// 온톨로지 페이지들
const WikiDocPage = lazyWithRetry(() => import('../pages/wiki/WikiDocPage'));
const OntologyChatPage = lazyWithRetry(() => import('../pages/ontology/OntologyChatPage'));
const OntologyMonitoringPage = lazyWithRetry(() => import('../pages/ontology/OntologyMonitoringPage'));
const WikiHomePage = lazyWithRetry(() => import('../pages/wiki/WikiHomePage'));
const WikiGraphPage = lazyWithRetry(() => import('../pages/wiki/WikiGraphPage'));
const WikiAssistantPage = lazyWithRetry(() => import('../pages/wiki/WikiAssistantPage'));
const WikiEntityPage = lazyWithRetry(() => import('../pages/wiki/WikiEntityPage'));

// 마케팅 페이지들
const AdPerformancePage = lazyWithRetry(() => import('../pages/marketing/AdPerformancePage'));
const ChannelPnlPage = lazyWithRetry(() => import('../pages/marketing/ChannelPnlPage'));

// View 컴포넌트들 - 사용 빈도가 낮음
const ListView = lazyWithRetry(() => import('../components/views/ListView'));
const CalendarView = lazyWithRetry(() => import('../components/views/CalendarView'));
const PivotTableView = lazyWithRetry(() => import('../components/views/PivotTableView'));
const DashboardBuilder = lazyWithRetry(() => import('../components/views/DashboardBuilder'));
const StudioEditor = lazyWithRetry(() => import('../components/views/StudioEditor'));
const GridStudioEditor = lazyWithRetry(() => import('../components/views/GridStudioEditor'));
const ActionBuilder = lazyWithRetry(() => import('../components/views/ActionBuilder'));

// 최적화된 로딩 컴포넌트
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Spinner size="large" />
  </div>
);

// 내 설정 (전 직원) · 회사 관리 (관리자) — 두 입구 모두 SettingsShell 을 쓴다
const SettingsPage = lazyWithRetry(() => import('../pages/settings/SettingsPage'));
const ManagePage = lazyWithRetry(() => import('../pages/manage/ManagePage'));

// HR 시스템 (Phase 1: 구성원·조직)
const MemberProfilePage = lazyWithRetry(() => import('../pages/hr/profile/MemberProfilePage'));
const HrDashboardPage = lazyWithRetry(() => import('../pages/hr/HrDashboardPage'));
const MemberSearchPage = lazyWithRetry(() => import('../pages/hr/employees/MemberSearchPage'));
const OrgChartPage = lazyWithRetry(() => import('../pages/hr/org-chart/OrgChartPage'));
const OnboardingPage = lazyWithRetry(() => import('../pages/hr/onboarding/OnboardingPage'));
// Sprint 7A-3 개편 — TimeClockPage 기능을 MyAttendancePage (일간 뷰) 로 통합
const MyAttendancePage = lazyWithRetry(() => import('../pages/hr/my-attendance/MyAttendancePage'));
// 2026-10-12 — 내 연장근무·수당·연차를 한 화면에 (본인 전용)
const MyAllowanceLeavePage = lazyWithRetry(() => import('../pages/hr/my-status/MyAllowanceLeavePage'));
const TeamAttendancePage = lazyWithRetry(() => import('../pages/hr/team-attendance/TeamAttendancePage'));
const LeavesPage = lazyWithRetry(() => import('../pages/hr/leaves/LeavesPage'));
const LeaveRequestNewPage = lazyWithRetry(() => import('../pages/hr/leaves/LeaveRequestNewPage'));
const TeamSchedulePage = lazyWithRetry(() => import('../pages/hr/team-schedule/TeamSchedulePage'));
const ApprovalsHubPage = lazyWithRetry(() => import('../pages/hr/approvals/ApprovalsHubPage'));
// LeaveBalancesAdminPage 는 LeavesPage 의 '전직원 잔여' 탭에서만 임베드 (별도 라우트 제거 — 2026-05-04 사용자 결정)
// Sprint 7B-8 admin 페이지 3종
const HrResponsibilityPage = lazyWithRetry(() => import('../pages/hr/responsibility/ResponsibilityPage'));
// Sprint 7B-12A — 문서·증명서
const DocumentsPage = lazyWithRetry(() => import('../pages/hr/documents/DocumentsPage'));
// Sprint 7B-12B — 계약 관리
const ContractsPage = lazyWithRetry(() => import('../pages/hr/contracts/ContractsPage'));
// Sprint 7B-12C — 온/오프보딩
// Sprint 7B-13 (B) — 성장 & 평가
const GoalsPage = lazyWithRetry(() => import('../pages/hr/growth/GoalsPage'));
const ReviewsPage = lazyWithRetry(() => import('../pages/hr/growth/ReviewsPage'));
const EducationPage = lazyWithRetry(() => import('../pages/hr/growth/EducationPage'));
// Phase2 C2 — 회의 태스크 칸반 보드
const MeetingTaskBoardPage = lazyWithRetry(() => import('../pages/hr/growth/MeetingTaskBoardPage'));
const MyTodoPage = lazyWithRetry(() => import('../pages/hr/todo/MyTodoPage'));
// Sprint 7B-14 (C) — 보상 & 복지
const PayrollPage = lazyWithRetry(() => import('../pages/hr/compensation/PayrollPage'));
const BenefitsPage = lazyWithRetry(() => import('../pages/hr/compensation/BenefitsPage'));
const InsurancePage = lazyWithRetry(() => import('../pages/hr/compensation/InsurancePage'));
// Sprint 7B-15 (D) — 조직 인사이트
const TrendPage = lazyWithRetry(() => import('../pages/hr/insights/TrendPage'));
const ReportsPage = lazyWithRetry(() => import('../pages/hr/insights/ReportsPage'));
// Sprint 16 — Admin 페이지 3종
// Sprint 47-A/B — HR 통합 설정 + Legacy redirect
// Sprint 20 — 1on1 미팅 (공지사항/Trends/Kiosk 는 47-G 정리에서 제거됨)
const MeetingListPage = lazyWithRetry(() => import('../pages/hr/meetings/MeetingListPage'));
const MeetingDetailPage = lazyWithRetry(() => import('../pages/hr/meetings/MeetingDetailPage'));
const MeetingSettingsPage = lazyWithRetry(() => import('../pages/hr/meetings/MeetingSettingsPage'));
// Sprint 21 — HR 통합 알림센터
const HrNotificationsPage = lazyWithRetry(() => import('../pages/hr/extras/HrNotificationsPage'));
// Sprint 25 — 월간 HR 리포트
// Sprint 28 — 관계 그래프
// Sprint 29 — Slack 알림
// Sprint 38 — 알림 설정
// Sprint 45 — HR Health Check
const LegacyAdminRedirect = lazyWithRetry(() => import('../pages/admin/LegacyAdminRedirect'));
const HrSettingsLegacyRedirect = lazyWithRetry(() => import('../pages/hr/admin/LegacyAdminRedirect'));

// 메뉴 접근 권한 검사 래퍼 (단일 페이지 & 레이아웃 라우트 겸용)
//
// 판정은 utils/menuAccess 정본 하나만 쓴다. 예전에는 여기서 직접 해석하면서
// admin/superadmin 통과 분기가 빠져 있어, 관리자에게 부분 권한이 저장되면
// 백엔드는 통과시키는데 화면이 스스로 막아 버렸다.
const MenuRoute: React.FC<{ menuKey: string; children?: React.ReactNode }> = ({ menuKey, children }) => {
  const { state } = useAuth();

  if (!canAccessMenu(state.user, menuKey)) {
    return <Navigate to="/access-denied" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

// 에러 페이지
const NotFoundPage = lazyWithRetry(() => import('../pages/error/NotFoundPage'));

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* 공개 라우트 - 로그인은 즉시 로드 */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={
        <Suspense fallback={<LoadingFallback />}>
          <Register />
        </Suspense>
      } />
      <Route path="/forgot-password" element={
        <Suspense fallback={<LoadingFallback />}>
          <ForgotPassword />
        </Suspense>
      } />
      <Route path="/reset-password" element={
        <Suspense fallback={<LoadingFallback />}>
          <ResetPassword />
        </Suspense>
      } />
      <Route path="/access-denied" element={
        <Suspense fallback={<LoadingFallback />}>
          <AccessDenied />
        </Suspense>
      } />

      {/* 방문홈스타일링 공개 신청 — 외부 사이트 iframe 임베드용 비로그인 라우트.
          관리자 /home-styling 과 분리. SMS 인증 후 home_styling 리드 등록 + Slack/캘린더. */}
      <Route path="/homestyling-apply" element={
        <Suspense fallback={<LoadingFallback />}>
          <HomeStylingApplyPage />
        </Suspense>
      } />

      {/* 주문서 기본 양식 그리기 전용 — 서버가 브라우저로 열어 캡처한다(카톡 발주·설치요청 그림).
          데이터를 스스로 불러오지 않고 서버가 넣어 준 window.__ORDER_SHEET__ 만 그린다 → 비로그인이어도 노출 없음 */}
      <Route path="/render/order-sheet" element={
        <Suspense fallback={null}>
          <RenderOrderSheetPage />
        </Suspense>
      } />

      {/* 평생수선 공개 신청 — 아임웹 평생수선 페이지 iframe 임베드용 비로그인 라우트(직원 화면은 /repair-requests) */}
      <Route path="/repair-apply" element={
        <Suspense fallback={<LoadingFallback />}>
          <RepairApplyPage />
        </Suspense>
      } />

      {/* 맞춤 제작 동의 — 고객이 알림톡의 본인 전용 링크(/agree/토큰)로 여는 비로그인 라우트(판매주문 결제 안내).
          화면이 토큰을 읽고 주소를 /agree 로 바꾼다(/agree/* 가 둘 다 받는다). */}
      <Route path="/agree/*" element={
        <Suspense fallback={<LoadingFallback />}>
          <OrderConsentPage />
        </Suspense>
      } />

      {/* 외부 고객 주문/배송 조회 — 일반 로그인/메뉴와 완전 격리된 비로그인 라우트.
          /track 에서 휴대폰 인증 → /track/verify → /track/orders → /track/orders/:orderNumber.
          하위 라우트는 customer-tracking JWT(15분, sessionStorage) 가 있어야 접근 가능. */}
      <Route path="/track" element={
        <Suspense fallback={<LoadingFallback />}>
          <CtPhoneInputPage />
        </Suspense>
      } />
      <Route path="/track/verify" element={
        <Suspense fallback={<LoadingFallback />}>
          <CtCodeVerifyPage />
        </Suspense>
      } />
      <Route element={
        <Suspense fallback={<LoadingFallback />}>
          <RequireCustomerSession />
        </Suspense>
      }>
        <Route path="/track/orders" element={
          <Suspense fallback={<LoadingFallback />}>
            <CtOrderListPage />
          </Suspense>
        } />
        <Route path="/track/orders/:orderNumber" element={
          <Suspense fallback={<LoadingFallback />}>
            <CtOrderDetailPage />
          </Suspense>
        } />
        {/* 고객 설치요청: 인증(PhoneInput→CodeVerify) → 멀티스텝 위저드 → 완료.
            A(주문 있음)/B(주문 없음) 분기는 위저드 진입 시 자동 판단. */}
        <Route path="/track/install-request" element={
          <Suspense fallback={<LoadingFallback />}>
            <InstallRequestWizard />
          </Suspense>
        } />
        <Route path="/track/install-request/done" element={
          <Suspense fallback={<LoadingFallback />}>
            <InstallRequestDonePage />
          </Suspense>
        } />
        {/* 내 설치일정 확인·수정요청 (인증 후 자동 감지 진입) */}
        <Route path="/track/install-schedules" element={
          <Suspense fallback={<LoadingFallback />}>
            <MyInstallSchedulesPage />
          </Suspense>
        } />
        <Route path="/track/install-schedules/:id" element={
          <Suspense fallback={<LoadingFallback />}>
            <MyInstallScheduleDetailPage />
          </Suspense>
        } />
      </Route>

      {/* 보호된 라우트 */}
      <Route element={<ProtectedRoute requireAuth={true} />}>
        <Route element={<SimpleLayout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* 대시보드는 즉시 로드 */}
          <Route path="/dashboard" element={<MenuRoute menuKey="dashboard"><AdminDashboard /></MenuRoute>} />

          {/* 구매 관리 */}
          <Route path="/purchase" element={<MenuRoute menuKey="purchase" />}>
            <Route index element={<Navigate to="/purchase/list" replace />} />
            <Route path="list" element={
              <Suspense fallback={<LoadingFallback />}>
                <PurchaseListPage />
              </Suspense>
            } />
            <Route path="new" element={
              <Suspense fallback={<LoadingFallback />}>
                <PurchaseFormPage />
              </Suspense>
            } />
            <Route path="edit/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <PurchaseFormPage />
              </Suspense>
            } />
            <Route path="view/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <PurchaseOrderView />
              </Suspense>
            } />
            <Route path="bulk-print" element={
              <Suspense fallback={<LoadingFallback />}>
                <BulkPrintPage />
              </Suspense>
            } />
            <Route path="analytics" element={
              <Suspense fallback={<LoadingFallback />}>
                <AnalyticsDashboard />
              </Suspense>
            } />
          </Route>

          {/* 생산 관리 */}
          <Route path="/production" element={<MenuRoute menuKey="production" />}>
            <Route index element={
              <Suspense fallback={<LoadingFallback />}>
                <ProductionListPage />
              </Suspense>
            } />
            <Route path="new" element={
              <Suspense fallback={<LoadingFallback />}>
                <ProductionFormPage />
              </Suspense>
            } />
            <Route path="edit/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <ProductionFormPage />
              </Suspense>
            } />
            <Route path="view/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <ProductionOrderView />
              </Suspense>
            } />
            <Route path="print" element={
              <Suspense fallback={<LoadingFallback />}>
                <ProductionPrintPage />
              </Suspense>
            } />
          </Route>

          {/* 연락처 관리 */}
          <Route path="/contacts" element={
            <MenuRoute menuKey="contacts">
              <Suspense fallback={<LoadingFallback />}>
                <ContactListPage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 판매 관리 */}
          <Route path="/sales" element={<MenuRoute menuKey="sales" />}>
            <Route index element={<Navigate to="/sales/list" replace />} />
            <Route path="list" element={
              <Suspense fallback={<LoadingFallback />}>
                <SalesListPage />
              </Suspense>
            } />
            <Route path="new" element={
              <Suspense fallback={<LoadingFallback />}>
                <SalesFormPage />
              </Suspense>
            } />
            <Route path="edit/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <SalesFormPage />
              </Suspense>
            } />
            <Route path="view/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <SalesViewPage />
              </Suspense>
            } />
            <Route path="order-view/:id" element={
              <Suspense fallback={<LoadingFallback />}>
                <SalesOrderView />
              </Suspense>
            } />
          </Route>

          {/* 해피콜 */}
          <Route path="/happycall" element={
            <MenuRoute menuKey="happycall">
              <Suspense fallback={<LoadingFallback />}>
                <HappyCallManagement />
              </Suspense>
            </MenuRoute>
          } />

          {/* 재고 관리 */}
          <Route path="/inventory" element={<MenuRoute menuKey="inventory" />}>
            <Route index element={
              <Suspense fallback={<LoadingFallback />}>
                <ProductListPage />
              </Suspense>
            } />
            <Route path="history" element={
              <Suspense fallback={<LoadingFallback />}>
                <InventoryHistoryPage />
              </Suspense>
            } />
            <Route path="count" element={
              <Suspense fallback={<LoadingFallback />}>
                <InventoryCountListPage />
              </Suspense>
            } />
            {/* count/:id 별도 경로 제거 — /inventory 탭 내 인라인 표시 */}
          </Route>

          {/* BOM 관리 */}
          <Route path="/bom" element={
            <MenuRoute menuKey="bom">
              <Suspense fallback={<LoadingFallback />}>
                <BOMManagement />
              </Suspense>
            </MenuRoute>
          } />

          {/* 방문홈스타일링 */}
          <Route path="/home-styling" element={
            <MenuRoute menuKey="home-styling">
              <Suspense fallback={<LoadingFallback />}>
                <HomeStylePage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 설치일정 */}
          <Route path="/installation-scheduling" element={
            <MenuRoute menuKey="installation-scheduling">
              <Suspense fallback={<LoadingFallback />}>
                <InstallationSchedulingPage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 클레임 */}
          <Route path="/claims" element={
            <MenuRoute menuKey="claims">
              <Suspense fallback={<LoadingFallback />}>
                <ClaimsPage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 평생수선 — 권한은 클레임과 같다(menuData permissionKey) */}
          <Route path="/repair-requests" element={
            <MenuRoute menuKey="repair-requests">
              <Suspense fallback={<LoadingFallback />}>
                <RepairRequestsPage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 현금영수증 발행 요청 목록 */}

          {/* 리퍼브 (재고 탭으로 이동) */}


          {/* [F12] 통계 대시보드(/statistics) 라우트 제거 — 통계는 업무 페이지 내부 뷰로 통합, 백엔드 API 유지 */}

          {/* Claude 변경 요청 승인 — 메뉴 권한 없이 로그인만(서버가 본인 요청서만 돌려준다). 사이드바 메뉴 없음 */}
          <Route path="/mcp-approvals" element={
            <Suspense fallback={<LoadingFallback />}>
              <McpApprovalsPage />
            </Suspense>
          } />

          {/* 알림 */}
          <Route path="/notifications" element={
            <Suspense fallback={<LoadingFallback />}>
              <NotificationsPage />
            </Suspense>
          } />

          {/* 온톨로지 */}
          <Route path="/ontology/chat" element={<MenuRoute menuKey="ontology-chat"><Suspense fallback={<LoadingFallback />}><OntologyChatPage /></Suspense></MenuRoute>} />
          <Route path="/ontology/monitoring" element={<MenuRoute menuKey="ontology-monitoring"><Suspense fallback={<LoadingFallback />}><OntologyMonitoringPage /></Suspense></MenuRoute>} />
          {/* 업무 위키 — 엔티티 경로가 홈보다 구체적이므로 먼저 선언한다 */}
          <Route path="/wiki/e/:type/:id" element={<MenuRoute menuKey="wiki"><Suspense fallback={<LoadingFallback />}><WikiEntityPage /></Suspense></MenuRoute>} />
          <Route path="/wiki/d/:slug" element={<MenuRoute menuKey="wiki"><Suspense fallback={<LoadingFallback />}><WikiDocPage /></Suspense></MenuRoute>} />
          <Route path="/wiki/graph" element={<MenuRoute menuKey="wiki"><Suspense fallback={<LoadingFallback />}><WikiGraphPage /></Suspense></MenuRoute>} />
          {/* 2026-09-21: 지식문서·데이터관계·파일함 화면을 폐지했다. /ontology/* 중 남은 것은 chat·monitoring 둘뿐이다.
              ⛔ 표(knowledge_documents·entities·relations)와 서비스는 **남아 있다** — AI 어시스턴트가 읽는다. */}
          {/* 어시스턴트가 업무 위키 첫 탭이 되므로 접근도 wiki 권한으로 통일(대표 요청 2026-08-19) */}
          <Route path="/wiki/assistant" element={<MenuRoute menuKey="wiki"><Suspense fallback={<LoadingFallback />}><WikiAssistantPage /></Suspense></MenuRoute>} />
          <Route path="/wiki" element={<MenuRoute menuKey="wiki"><Suspense fallback={<LoadingFallback />}><WikiHomePage /></Suspense></MenuRoute>} />
          {/* 자동화 */}
          <Route path="/automation/order-collection" element={
            <MenuRoute menuKey="automation-orders">
              <Suspense fallback={<LoadingFallback />}>
                <OrderCollectionPage />
              </Suspense>
            </MenuRoute>
          } />
          <Route path="/automation/tasks" element={
            <MenuRoute menuKey="automation-tasks">
              <Suspense fallback={<LoadingFallback />}>
                <AutomationTasksPage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 회의록 녹음 (S6) — ★기능 게이트(기본 OFF)★ 플래그 ON 일 때만 라우트 등록(OFF면 미등록 → NotFound) */}
          {MEETING_NOTES_ENABLED && (
            <Route path="/meeting-notes" element={<MenuRoute menuKey="meeting-notes" />}>
              {/* [A2] 회의록 «목록» 은 폐지 — 미팅 화면의 회의록 탭으로 넘긴다.
                  2026-09-21 정리: 들어갈 입구가 없던 usage·voice-samples 만 제거했다.
                  ⛔ 아래 넷은 «쓰이고 있어» 남긴다 — 지우면 살아 있는 버튼이 깨진다:
                    · index      — dictionary 화면의 «회의록으로 돌아가기» 목적지
                    · new        — 인사 회의목록의 녹음 버튼 2곳 (Codex PASS2 [C-7])
                    · dictionary — 상세(:id) 화면의 «용어 사전 열기» 버튼
                    · :id        — 지난 PROD 알림 10건이 직접 가리킨다 (Codex PASS1 [C-2]) */}
              <Route index element={<Navigate to="/hr/meetings?view=notes" replace />} />
              <Route path="new" element={
                <Suspense fallback={<LoadingFallback />}>
                  <MeetingRecordPage />
                </Suspense>
              } />
              {/* S8 정적 하위 라우트 — :id 보다 먼저(정적 우선) */}
              <Route path="dictionary" element={
                <Suspense fallback={<LoadingFallback />}>
                  <MeetingDictionaryPage />
                </Suspense>
              } />
              {/* 전사 검수 + 발행 (S7) — :id 는 static 'new'·정적 하위 뒤(정적 우선) */}
              <Route path=":id" element={
                <Suspense fallback={<LoadingFallback />}>
                  <TranscriptReviewPage />
                </Suspense>
              } />
            </Route>
          )}

          {/* 마케팅 & 정산 */}
          <Route path="/marketing/ad-performance" element={
            <MenuRoute menuKey="marketing-ad">
              <Suspense fallback={<LoadingFallback />}>
                <AdPerformancePage />
              </Suspense>
            </MenuRoute>
          } />
          <Route path="/marketing/channel-pnl" element={
            <MenuRoute menuKey="marketing-channel-pnl">
              <Suspense fallback={<LoadingFallback />}>
                <ChannelPnlPage />
              </Suspense>
            </MenuRoute>
          } />

          {/* 내 설정 — 전 직원. 주소 /settings/user 는 그대로 두고 아래로만 조각을 붙인다 */}
          <Route path="/settings">
            <Route index element={<Navigate to="/settings/user" replace />} />
            <Route path="user" element={
              <Suspense fallback={<LoadingFallback />}>
                <SettingsPage />
              </Suspense>
            } />
            <Route path="user/:category" element={
              <Suspense fallback={<LoadingFallback />}>
                <SettingsPage />
              </Suspense>
            } />
            <Route path="user/:category/:section" element={
              <Suspense fallback={<LoadingFallback />}>
                <SettingsPage />
              </Suspense>
            } />
            {/* API 문서는 백엔드 주소 전체 목록이라 관리자 영역으로 옮겼다 */}
            <Route path="api-docs" element={<Navigate to="/manage/system/api-docs" replace />} />
          </Route>

          {/* HR 시스템 (Phase 1 Skeleton — 모든 페이지 ComingSoon, S3~S6에서 순차 구현) */}
          <Route path="/hr" element={<Navigate to="/hr/me" replace />} />
          <Route path="/hr/me" element={
            <Suspense fallback={<LoadingFallback />}>
              <MemberProfilePage mode="me" />
            </Suspense>
          } />
          <Route path="/hr/dashboard" element={
            <Suspense fallback={<LoadingFallback />}>
              <HrDashboardPage />
            </Suspense>
          } />
          <Route path="/hr/search" element={
            <Suspense fallback={<LoadingFallback />}>
              <MemberSearchPage />
            </Suspense>
          } />
          <Route path="/hr/employees/:id" element={
            <Suspense fallback={<LoadingFallback />}>
              <MemberProfilePage mode="full" />
            </Suspense>
          } />
          <Route path="/hr/org-chart" element={
            <Suspense fallback={<LoadingFallback />}>
              <OrgChartPage />
            </Suspense>
          } />
          <Route path="/hr/onboarding" element={
            <Suspense fallback={<LoadingFallback />}>
              <OnboardingPage />
            </Suspense>
          } />
          {/* Sprint 7A-3 — /hr/time-clock → /hr/my-attendance 일간 뷰 통합 */}
          <Route path="/hr/time-clock" element={<Navigate to="/hr/my-attendance" replace />} />
          <Route path="/hr/my-attendance" element={
            <Suspense fallback={<LoadingFallback />}>
              <MyAttendancePage />
            </Suspense>
          } />
          <Route path="/hr/my-status" element={
            <Suspense fallback={<LoadingFallback />}>
              <MyAllowanceLeavePage />
            </Suspense>
          } />
          <Route path="/hr/team-attendance" element={
            <Suspense fallback={<LoadingFallback />}>
              <TeamAttendancePage />
            </Suspense>
          } />
          <Route path="/hr/leaves" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeavesPage />
            </Suspense>
          } />
          <Route path="/hr/leaves/new" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeaveRequestNewPage />
            </Suspense>
          } />
          <Route path="/hr/leaves/new/:typeCode" element={
            <Suspense fallback={<LoadingFallback />}>
              <LeaveRequestNewPage />
            </Suspense>
          } />
          <Route path="/hr/team-schedule" element={
            <Suspense fallback={<LoadingFallback />}>
              <TeamSchedulePage />
            </Suspense>
          } />
          <Route path="/hr/approvals" element={
            <Suspense fallback={<LoadingFallback />}>
              <ApprovalsHubPage />
            </Suspense>
          } />
          {/* 옛 HR 설정 주소 흡수 → 「회사 관리」 /manage/*.
              옛 category/section 조각을 새 자리로 옮기는 표는 routes/legacyAdminMap.ts 에 있다.
              ⛔ 이 라우트들은 로그인만 하면 되는 블록 안에 있다 — 화면을 그리지 않고
                 리다이렉트만 하므로 안전하다. 실제 화면은 /manage 의 role 게이트가 지킨다. */}
          <Route path="/hr/admin/settings" element={<Navigate to={MANAGE_DEFAULT_PATH} replace />} />
          <Route path="/hr/admin/settings/:category" element={
            <Suspense fallback={<LoadingFallback />}><HrSettingsLegacyRedirect /></Suspense>
          } />
          <Route path="/hr/admin/settings/:category/:section" element={
            <Suspense fallback={<LoadingFallback />}><HrSettingsLegacyRedirect /></Suspense>
          } />
          {/* 일반 사용자용 — admin 영역 아님 (보존) */}
          <Route path="/hr/contracts" element={
            <Suspense fallback={<LoadingFallback />}>
              <ContractsPage />
            </Suspense>
          } />
          {/* 회의 — 예약·진행·회의록. 옛 1on1 경로는 여기로 넘긴다(북마크·알림 링크 보존). */}
          <Route path="/hr/meetings">
            <Route index element={
              <Suspense fallback={<LoadingFallback />}>
                <MeetingListPage />
              </Suspense>
            } />
            {/* ⛔ 정적 경로가 :id 보다 먼저여야 한다 */}
            <Route path="settings" element={
              <Suspense fallback={<LoadingFallback />}>
                <MeetingSettingsPage />
              </Suspense>
            } />
            <Route path=":id" element={
              <Suspense fallback={<LoadingFallback />}>
                <MeetingDetailPage />
              </Suspense>
            } />
          </Route>
          <Route path="/hr/oneonones" element={<Navigate to="/hr/meetings" replace />} />
          {/* /hr/announcements 는 47-G 정리에서 제거됨 */}
          <Route path="/hr/notifications" element={
            <Suspense fallback={<LoadingFallback />}>
              <HrNotificationsPage />
            </Suspense>
          } />
          {/* admin/{monthly-reports,relationship-graph,slack-dispatch} → settings 통합 (LegacyAdminRedirect 가 처리) */}
          {/* /hr/me/trends 는 47-G 정리에서 제거됨 */}
          {/* 2026-08-19 F-7 — 알림 설정 정본을 /settings/user 로 일원화.
              기존 링크·북마크를 보호하려고 경로는 남기고 리다이렉트만 한다. */}
          <Route path="/hr/me/notifications-settings" element={<Navigate to="/settings/user/preferences/notifications" replace />} />
          {/* admin/{kiosk,health-check} — kiosk 는 47-G 정리, health-check 는 settings 통합 */}
          <Route path="/hr/documents" element={
            <Suspense fallback={<LoadingFallback />}>
              <DocumentsPage />
            </Suspense>
          } />
          {/* admin/{document-templates,company-documents,document-issuances} → settings 통합 (LegacyAdminRedirect 가 처리) */}
          <Route path="/hr/goals" element={
            <Suspense fallback={<LoadingFallback />}>
              <GoalsPage />
            </Suspense>
          } />
          <Route path="/hr/reviews" element={
            <Suspense fallback={<LoadingFallback />}>
              <ReviewsPage />
            </Suspense>
          } />
          <Route path="/hr/todo" element={
            <Suspense fallback={<LoadingFallback />}>
              <MyTodoPage />
            </Suspense>
          } />
          <Route path="/hr/tasks" element={
            <Suspense fallback={<LoadingFallback />}>
              <MeetingTaskBoardPage />
            </Suspense>
          } />
          <Route path="/hr/education" element={
            <Suspense fallback={<LoadingFallback />}>
              <EducationPage />
            </Suspense>
          } />
          <Route path="/hr/payroll" element={
            <Suspense fallback={<LoadingFallback />}>
              <PayrollPage />
            </Suspense>
          } />
          <Route path="/hr/benefits" element={
            <Suspense fallback={<LoadingFallback />}>
              <BenefitsPage />
            </Suspense>
          } />
          <Route path="/hr/insurance" element={
            <Suspense fallback={<LoadingFallback />}>
              <InsurancePage />
            </Suspense>
          } />
          <Route path="/hr/trend" element={
            <Suspense fallback={<LoadingFallback />}>
              <TrendPage />
            </Suspense>
          } />
          <Route path="/hr/reports" element={
            <Suspense fallback={<LoadingFallback />}>
              <ReportsPage />
            </Suspense>
          } />
          <Route path="/hr/responsibility" element={
            <Suspense fallback={<LoadingFallback />}>
              <HrResponsibilityPage />
            </Suspense>
          } />
          {/* 옛 HR 관리자 화면 — 메뉴에서 갈 길이 없던 화면. 계정 관리로 흡수 */}
          <Route path="/hr/admin" element={<Navigate to="/manage/people/accounts" replace />} />
          {/*
            Legacy admin URL → 통합 settings 자동 리다이렉트.
            매핑은 frontend/src/routes/legacyAdminMap.ts 한 파일에 집약.
            반드시 /hr/admin/settings/* 보다 뒤에 둬야 nested 라우트 우선 매칭됨.
          */}
          <Route path="/hr/admin/:legacyKey" element={
            <Suspense fallback={<LoadingFallback />}><HrSettingsLegacyRedirect /></Suspense>
          } />

          {/* Views - 낮은 우선순위 */}
          <Route path="/views">
            <Route path="list" element={
              <Suspense fallback={<LoadingFallback />}>
                <ListView 
                  columns={[
                    { field: 'name', headerName: 'Name', sortable: true },
                    { field: 'status', headerName: 'Status', sortable: true },
                    { field: 'date', headerName: 'Date', sortable: true }
                  ]}
                  rows={[]}
                />
              </Suspense>
            } />
            <Route path="calendar" element={
              <Suspense fallback={<LoadingFallback />}>
                <CalendarView 
                  events={[]}
                  onEventAdd={(event) => console.log('Add event:', event)}
                  onEventUpdate={(event) => console.log('Update event:', event)}
                />
              </Suspense>
            } />
            <Route path="pivot" element={
              <Suspense fallback={<LoadingFallback />}>
                <PivotTableView 
                  data={[]}
                  columns={[]}
                />
              </Suspense>
            } />
            <Route path="dashboard-builder" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardBuilder />
              </Suspense>
            } />
            <Route path="studio" element={
              <Suspense fallback={<LoadingFallback />}>
                <StudioEditor />
              </Suspense>
            } />
            <Route path="grid-studio" element={
              <Suspense fallback={<LoadingFallback />}>
                <GridStudioEditor moduleId="demo" viewType="list" />
              </Suspense>
            } />
            <Route path="action-builder" element={
              <Suspense fallback={<LoadingFallback />}>
                <ActionBuilder />
              </Suspense>
            } />
          </Route>
        </Route>
      </Route>

      {/* ── 관리자 전용 ────────────────────────────────────────────────
          「회사 관리」 /manage 가 통합 입구다. 예전 세 입구(/admin · /hr/admin ·
          /hr/admin/settings)는 전부 여기로 흡수된다.

          ⛔ 이 블록의 role 게이트가 유일한 화면 방어선이다. 통합 전에는 /admin 에만
             게이트가 있고 /hr/admin/settings 는 로그인만 하면 열려서, 일반 직원이
             주소만 치면 급여 정책·권한 화면이 그대로 열렸다.                        */}
      <Route element={<ProtectedRoute requireAuth={true} roles={['admin', 'superadmin']} />}>
        <Route element={<SimpleLayout />}>
          <Route path="/manage" element={
            <Suspense fallback={<LoadingFallback />}><ManagePage /></Suspense>
          } />
          <Route path="/manage/:category" element={
            <Suspense fallback={<LoadingFallback />}><ManagePage /></Suspense>
          } />
          <Route path="/manage/:category/:section" element={
            <Suspense fallback={<LoadingFallback />}><ManagePage /></Suspense>
          } />

          {/* 옛 ERP 관리 주소 흡수 — ?tab=<슬러그> 7종을 /manage/* 로 옮긴다 */}
          <Route path="/admin" element={
            <Suspense fallback={<LoadingFallback />}><LegacyAdminRedirect /></Suspense>
          } />
          <Route path="/admin/design-system" element={
            <Navigate to="/manage/system/design-system" replace />
          } />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={
        <Suspense fallback={<LoadingFallback />}>
          <NotFoundPage />
        </Suspense>
      } />
    </Routes>
  );
};

export default AppRoutes;